import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconLock, IconUserCog, IconX } from '@tabler/icons-react';
import { FormattedMessage } from 'react-intl';

import { requestResetPassword } from 'api/auth';
import { editUserInfo } from 'api/user';
import useAuth from 'hooks/useAuth';
import { useDispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';

const getErrorMessage = (error, fallback) => error?.message || error?.error || fallback;

/**
 * Displays personal account settings in a floating modal.
 *
 * @param {Object} props - Component props.
 * @param {boolean} props.open - Whether the profile dialog is visible.
 * @param {Function} props.onClose - Closes the profile dialog.
 * @returns {JSX.Element}
 */
const Profile = ({ open, onClose }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { refetchSession, user } = useAuth();
    const [profile, setProfile] = useState({
        firstName: user?.name?.first || '',
        lastName: user?.name?.last || '',
        email: user?.email || ''
    });
    const [saving, setSaving] = useState(false);
    const [passwordOpen, setPasswordOpen] = useState(false);
    const [passwordBusy, setPasswordBusy] = useState(false);

    // The authenticated session is the canonical source for profile identity data.
    useEffect(() => {
        setProfile({
            firstName: user?.name?.first || '',
            lastName: user?.name?.last || '',
            email: user?.email || ''
        });
    }, [user]);

    const showMessage = (message, color = 'success') => {
        dispatch(
            openSnackbar({
                open: true,
                message,
                variant: 'alert',
                alert: { color }
            })
        );
    };

    const handleClose = () => {
        if (saving || passwordBusy) return;
        setPasswordOpen(false);
        onClose();
    };

    const handleProfileChange = (event) => {
        setProfile((current) => ({ ...current, [event.target.name]: event.target.value }));
    };

    const handleSaveProfile = async (event) => {
        event.preventDefault();

        if (!profile.firstName.trim() || !profile.lastName.trim()) {
            showMessage('First name and last name are required.', 'error');
            return;
        }

        setSaving(true);
        try {
            const response = await editUserInfo({
                firstName: profile.firstName,
                lastName: profile.lastName
            });
            setProfile(response.data);
            await refetchSession();
            showMessage(response.message || 'Profile updated successfully.');
        } catch (error) {
            showMessage(getErrorMessage(error, 'Unable to save profile changes.'), 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleRequestPasswordReset = async () => {
        setPasswordBusy(true);

        try {
            const response = await requestResetPassword({ email: profile.email });
            setPasswordOpen(false);
            showMessage(response.message || `A secure password reset link was sent to ${profile.email}.`);
        } catch (error) {
            showMessage(getErrorMessage(error, 'Unable to send the password reset email.'), 'error');
        } finally {
            setPasswordBusy(false);
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        overflow: 'hidden',
                        maxHeight: 'calc(100vh - 48px)'
                    }
                }}
            >
                <DialogTitle sx={{ borderBottom: `1px solid ${theme.palette.divider}`, px: { xs: 2, sm: 3 }, py: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                        <Stack direction="row" alignItems="center" spacing={1.25}>
                            <IconUserCog size={26} />
                            <Typography variant="h2">
                                <FormattedMessage id="account-settings" />
                            </Typography>
                        </Stack>
                        <IconButton aria-label="Close account settings" onClick={handleClose} disabled={saving || passwordBusy}>
                            <IconX size={22} />
                        </IconButton>
                    </Stack>
                </DialogTitle>

                <DialogContent sx={{ p: 0 }}>
                    <Grid container>
                        <Grid item xs={12} sx={{ p: { xs: 2, sm: 3 } }}>
                            <Stack spacing={3}>
                                <Box>
                                    <Typography variant="h3">User Profile</Typography>
                                    <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                                        Manage your personal account information.
                                    </Typography>
                                </Box>

                                <Box component="form" onSubmit={handleSaveProfile}>
                                    <Grid container spacing={2.5}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                required
                                                label="First Name"
                                                name="firstName"
                                                value={profile.firstName}
                                                onChange={handleProfileChange}
                                                inputProps={{ maxLength: 100 }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                required
                                                label="Last Name"
                                                name="lastName"
                                                value={profile.lastName}
                                                onChange={handleProfileChange}
                                                inputProps={{ maxLength: 100 }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                disabled
                                                label="Email Address"
                                                name="email"
                                                value={profile.email}
                                                InputProps={{ readOnly: true }}
                                                helperText="Your email address cannot be changed from here. Please contact your administrator for email address updates."
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Button type="submit" color="secondary" variant="contained" disabled={saving}>
                                                {saving ? <CircularProgress size={20} color="inherit" /> : 'Save Changes'}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Box>

                                <Divider />

                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    justifyContent="space-between"
                                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                                    spacing={2}
                                >
                                    <Box>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <IconLock size={20} />
                                            <Typography variant="h4">Password</Typography>
                                        </Stack>
                                        <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                                            A secure email link is required before changing your password.
                                        </Typography>
                                    </Box>
                                    <Button variant="outlined" color="secondary" onClick={() => setPasswordOpen(true)}>
                                        Change Password
                                    </Button>
                                </Stack>
                            </Stack>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>

            <Dialog open={passwordOpen} onClose={passwordBusy ? undefined : () => setPasswordOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Verify your email</DialogTitle>
                <DialogContent dividers>
                    <Typography color="text.secondary">
                        Send a secure password reset link to {profile.email}. The link must be verified before a new password can be
                        entered.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPasswordOpen(false)} color="secondary" disabled={passwordBusy}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="secondary" onClick={handleRequestPasswordReset} disabled={passwordBusy}>
                        {passwordBusy ? <CircularProgress size={20} color="inherit" /> : 'Send Secure Link'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

Profile.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired
};

export default Profile;
